import { ApiError } from "@sidegigllc/api";
import type { ForManagerLocationDetail } from "@sidegigllc/api/client";
import type { QueryClient } from "@tanstack/react-query";
import { createRoute, redirect } from "@tanstack/react-router";
import { activeLocationsQueryOptions, locationQueryOptions } from "api/queries/locations";
import { homePathFor } from "domains/auth/access/roles";
import { getStoredLocationId, swapLocationInPath } from "services/atoms";
import * as z from "zod/mini";
import { signedUpRoute } from "./signedUp";

// Admins reach any location, so trust their stored selection even before it lands in the active list;
// non-admins keep the membership check so a revoked id can't stick.
export const pickLocationId = (
  locations: ReadonlyArray<{ id: number }>,
  stored: number | undefined,
  isAdmin: boolean,
) => {
  const storedIsValid = stored !== undefined && Number.isInteger(stored) && stored > 0;
  return storedIsValid && (isAdmin || locations.some((l) => l.id === stored)) ? stored : locations[0].id;
};

// 404 (deleted/inaccessible location) → bounce to the list; a raw throw is Sentry-reported by the
// router, bypassing processError's 404 drop.
const ensureLocation = async (queryClient: QueryClient, locationId: number): Promise<ForManagerLocationDetail> => {
  try {
    return await queryClient.query({ ...locationQueryOptions(locationId), staleTime: "static" });
  } catch (error) {
    if (error instanceof ApiError && error.isNotFound()) {
      throw redirect({ href: "/locations" });
    }
    throw error;
  }
};

export const rootIndexRoute = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "/",
  beforeLoad: async ({ context: { auth, queryClient } }) => {
    const locations = await queryClient.query({ ...activeLocationsQueryOptions(), staleTime: "static" });
    if (locations.length === 0) throw redirect({ href: "/locations" });
    throw redirect({
      href: homePathFor(auth.user, pickLocationId(locations, getStoredLocationId(), auth.user.is_gigpro_admin)),
    });
  },
});

export const locationsListRoute = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "locations",
  validateSearch: z.object({ resumeWith: z.optional(z.number()) }),
});

export const locationLayoutRoute = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "locations/$locationId",
  beforeLoad: async ({ context: { auth, queryClient }, params, location }) => {
    const locationId = Number(params.locationId);
    const locations = await queryClient.query({ ...activeLocationsQueryOptions(), staleTime: "static" });

    if (locations.length === 0) {
      // First-shift post flow must stay reachable before any location is active.
      if (location.pathname.endsWith("/gigs-add")) return {};
      throw redirect({ href: "/locations" });
    }

    // -1 sentinel (no location chosen yet) → resolve stored/first and rewrite the URL.
    if (!(locationId > 0)) {
      // A non-numeric segment has no id to swap; rewriting would loop on the same URL.
      if (Number.isNaN(locationId)) throw redirect({ href: "/locations" });
      const target = pickLocationId(locations, getStoredLocationId(), auth.user.is_gigpro_admin);
      throw redirect({ href: `${swapLocationInPath(location.pathname, locationId, target)}${location.searchStr}` });
    }

    return { location: await ensureLocation(queryClient, locationId) };
  },
});
